/**
 * Shipping protection toggle.
 *
 * Adds or removes the hidden protection variant from the cart. The amount
 * charged is set server side by the Cart Transform function; this element only
 * owns the line's presence and the shopper's opt-out.
 */
(function () {
  if (customElements.get("shipping-protection")) return;

  // True while our own cart update is in flight. Reconciling against the
  // markup that request is about to replace would race it.
  let inFlight = false;

  /**
   * The element is replaced on every cart refresh, so it asks for a reconcile
   * each time it lands in the page: on load, after a product form fills the
   * drawer, and after a quantity change. Refreshes caused by our own request
   * land while inFlight is true and are ignored. A cart already in the right
   * state does nothing, so this cannot loop.
   */
  function scheduleReconcile() {
    setTimeout(function () {
      if (inFlight) return;
      const element = document.querySelector("shipping-protection");
      if (element && typeof element.reconcile === "function") {
        element.reconcile();
      }
    });
  }

  customElements.define(
    "shipping-protection",
    class ShippingProtection extends HTMLElement {
      connectedCallback() {
        this.variantId = this.dataset.variantId;
        this.sectionId = this.dataset.sectionId;
        this.inDrawer = this.hasAttribute("data-in-drawer");
        this.active = this.hasAttribute("data-active");
        this.quantity = parseInt(this.dataset.quantity, 10) || 0;
        this.optedOut = this.hasAttribute("data-opted-out");
        this.hasPhysical = this.hasAttribute("data-has-physical");
        this.busy = false;

        const button = this.querySelector("[data-sp-toggle]");
        if (button) button.addEventListener("click", this.onToggle.bind(this));

        if (!inFlight) scheduleReconcile();
      }

      /**
       * Keeps the cart honest without the shopper doing anything: adds the line
       * when protection is on by default, and drops it once the cart no longer
       * holds anything that ships.
       */
      reconcile() {
        if (this.busy) return;
        if (this.active && !this.hasPhysical) return this.setQuantity(0, null);
        if (!this.active && this.hasPhysical && !this.optedOut) {
          return this.setQuantity(1, null);
        }
        // One line, always. The fee is the whole cart's, not a per-unit charge,
        // so a line that ended up holding several of them is pulled back to one.
        // The correction leaves a breadcrumb on the cart so an affected order
        // says where it happened, not just that it did.
        if (this.active && this.quantity > 1) {
          return this.setQuantity(1, null, {
            _shipping_protection_qty: this.anomalyNote(),
          });
        }
      }

      /**
       * Short enough to read at a glance on the order, and underscore prefixed
       * so Shopify keeps it off the shopper's confirmation.
       */
      anomalyNote() {
        let from = "-";
        try {
          if (document.referrer) from = new URL(document.referrer).pathname;
        } catch (error) {
          // A referrer we cannot parse tells us nothing; the rest still does.
        }
        return [
          "qty=" + this.quantity,
          "at=" + window.location.pathname,
          "from=" + from,
          "drawer=" + (this.inDrawer ? "1" : "0"),
          new Date().toISOString(),
        ].join(" ");
      }

      onToggle() {
        if (this.busy) return;
        return this.active
          ? this.setQuantity(0, "true")
          : this.setQuantity(1, "");
      }

      /**
       * Everything goes through /cart/update.js: it sets an exact quantity
       * instead of incrementing, and it is the only cart endpoint that accepts
       * attributes in the same request. `optOut` of null leaves the shopper's
       * stored choice untouched, and marks a change the shopper did not ask for.
       */
      setQuantity(quantity, optOut, extraAttributes) {
        this.busy = true;
        inFlight = true;
        this.setAttribute("aria-busy", "true");

        const targets = this.sectionsToRender();
        const payload = { updates: {} };
        payload.updates[this.variantId] = quantity;
        if (optOut !== null) {
          payload.attributes = { shipping_protection_opt_out: optOut };
        }
        if (extraAttributes) {
          payload.attributes = Object.assign(
            {},
            payload.attributes,
            extraAttributes,
          );
        }
        payload.sections = [...new Set(targets.map((t) => t.section))];
        payload.sections_url = window.location.pathname;

        return fetch(
          window.routes.cart_update_url,
          Object.assign({}, fetchConfig(), {
            body: JSON.stringify(payload),
          }),
        )
          .then((response) => response.json())
          .then((json) => {
            if (json.status || json.errors) {
              throw new Error(json.description || json.message);
            }
            this.renderSections(json.sections, targets);
            this.syncEmptyState(json.item_count === 0);
          })
          .catch(() => {
            // The cart is the source of truth; a reload beats a wrong toggle.
            // Only for a click: an automatic add that keeps failing would
            // otherwise reload the page forever.
            if (optOut !== null) window.location.reload();
          })
          .finally(() => {
            this.busy = false;
            inFlight = false;
            this.removeAttribute("aria-busy");
          });
      }

      renderSections(rendered, targets) {
        if (!rendered) return;
        targets.forEach((target) => {
          const html = rendered[target.section];
          if (!html) return;
          const source = new DOMParser()
            .parseFromString(html, "text/html")
            .querySelector(target.selector);
          const host = document.getElementById(target.id);
          const destination =
            host && (host.querySelector(target.selector) || host);
          if (source && destination) destination.innerHTML = source.innerHTML;
        });
      }

      /**
       * Dropping the line can empty the cart. These classes sit outside the
       * markup we replace, and the theme only updates them from its own
       * requests.
       */
      syncEmptyState(isEmpty) {
        const toggle = (selector, name) => {
          const element = document.querySelector(selector);
          if (element) element.classList.toggle(name, isEmpty);
        };
        toggle("cart-drawer", "is-empty");
        toggle("cart-drawer-items", "is-empty");
        toggle("cart-items", "is-empty");
        toggle("#main-cart-footer", "is-empty");
        toggle(".main-cart__content", "is-empty-parent");
      }

      /**
       * The cart page does not render the drawer, so only one of the two is
       * ever on the page. Same ids, sections and selectors the theme's own cart
       * code refreshes.
       */
      sectionsToRender() {
        const bubble = {
          id: "cart-icon-bubble",
          section: "cart-icon-bubble",
          selector: ".shopify-section",
        };

        if (this.inDrawer) {
          return [
            bubble,
            {
              id: "CartDrawer",
              section: this.sectionId,
              selector: ".drawer__inner",
            },
          ];
        }

        return [
          bubble,
          {
            id: "main-cart-items",
            section: this.sectionId,
            selector: ".js-contents",
          },
          {
            id: "main-cart-hw-notice",
            section: this.sectionId,
            selector: ".js-contents-hw",
          },
          {
            id: "main-cart-footer",
            section: this.sectionId,
            selector: ".js-contents-totals",
          },
        ];
      }
    },
  );

  // A cart that changes while the drawer stays open never reloads the page.
  if (typeof subscribe === "function") {
    subscribe(PUB_SUB_EVENTS.cartUpdate, scheduleReconcile);
  }
})();
